import React from 'react';
import Icon from 'components/AppIcon';

const SymptomsSection = ({ data = {}, onChange, errors = [] }) => {
  const handleMultiSelectChange = (field, value, checked) => {
    const currentValues = data[field] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);
    onChange({ [field]: newValues });
  };

  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const physicalSymptoms = [
    { value: 'palpitations', label: 'Heart palpitations or rapid heartbeat', icon: 'Heart' },
    { value: 'tiredness', label: 'Persistent tiredness or fatigue', icon: 'Battery' },
    { value: 'headache', label: 'Frequent headaches', icon: 'Brain' },
    { value: 'body-ache', label: 'Body aches or muscle pain', icon: 'Zap' },
    { value: 'low-energy', label: 'Low energy levels', icon: 'BatteryLow' },
    { value: 'shortness-breath', label: 'Shortness of breath', icon: 'Wind' },
    { value: 'chest-pain', label: 'Chest pain or tightness', icon: 'Heart' },
    { value: 'dizziness', label: 'Dizziness or lightheadedness', icon: 'RotateCcw' },
    { value: 'nausea', label: 'Nausea or stomach issues', icon: 'Stomach' },
    { value: 'sweating', label: 'Excessive sweating', icon: 'Droplets' },
    { value: 'trembling', label: 'Trembling or shaking', icon: 'Zap' },
    { value: 'sleep-issues', label: 'Sleep disturbances', icon: 'Moon' },
    { value: 'appetite-changes', label: 'Changes in appetite', icon: 'Utensils' },
    { value: 'none', label: 'None of the above', icon: 'Check' }
  ];

  const symptomFrequency = [
    { value: 'daily', label: 'Daily' },
    { value: 'several-times-week', label: 'Several times a week' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'occasionally', label: 'Occasionally' },
    { value: 'rarely', label: 'Rarely' }
  ];

  const symptomSeverity = [
    { value: 'mild', label: 'Mild - Barely noticeable' },
    { value: 'moderate', label: 'Moderate - Noticeable but manageable' },
    { value: 'severe', label: 'Severe - Significantly impacts daily life' },
    { value: 'very-severe', label: 'Very Severe - Extremely difficult to manage' }
  ];

  const symptomTriggers = [
    { value: 'stress', label: 'Stressful situations' },
    { value: 'crowds', label: 'Crowded places' },
    { value: 'news', label: 'COVID-19 news or information' },
    { value: 'work', label: 'Work-related stress' },
    { value: 'social', label: 'Social situations' },
    { value: 'health-concerns', label: 'Health concerns' },
    { value: 'financial', label: 'Financial worries' },
    { value: 'isolation', label: 'Being alone or isolated' },
    { value: 'uncertainty', label: 'Uncertainty about the future' },
    { value: 'no-triggers', label: 'No specific triggers identified' }
  ];

  const hasSymptoms = data.physicalSymptoms && data.physicalSymptoms.length > 0 && !data.physicalSymptoms.includes('none');

  return (
    <div className="space-y-8">
      {/* Physical Symptoms */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Are you experiencing any of the following physical symptoms? *
        </label>
        <p className="text-sm text-text-secondary mb-4">
          Select all that apply. These symptoms may be related to stress, anxiety, or other health concerns.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {physicalSymptoms.map((symptom) => (
            <label
              key={symptom.value}
              className={`
                flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${(data.physicalSymptoms || []).includes(symptom.value)
                  ? 'border-primary bg-primary-50 text-primary-600' :'border-border bg-surface hover:bg-surface-secondary'
                }
              `}
            >
              <input
                type="checkbox"
                checked={(data.physicalSymptoms || []).includes(symptom.value)}
                onChange={(e) => handleMultiSelectChange('physicalSymptoms', symptom.value, e.target.checked)}
                className="sr-only"
              />
              <div className={`
                w-5 h-5 border-2 rounded mr-3 flex items-center justify-center
                ${(data.physicalSymptoms || []).includes(symptom.value)
                  ? 'border-primary bg-primary' :'border-border'
                }
              `}>
                {(data.physicalSymptoms || []).includes(symptom.value) && (
                  <Icon name="Check" size={12} color="white" />
                )}
              </div>
              <Icon name={symptom.icon} size={16} className="mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">{symptom.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Conditional sections - only show if symptoms are selected */}
      {hasSymptoms && (
        <>
          {/* Symptom Frequency */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              How frequently do you experience these symptoms?
            </label>
            <div className="space-y-2">
              {symptomFrequency.map((frequency) => (
                <label
                  key={frequency.value}
                  className={`
                    relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                    ${data.symptomFrequency === frequency.value
                      ? 'border-secondary bg-secondary-50 text-secondary-600' :'border-border bg-surface hover:border-border-focus'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="symptomFrequency"
                    value={frequency.value}
                    checked={data.symptomFrequency === frequency.value}
                    onChange={(e) => handleInputChange('symptomFrequency', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`
                    w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                    ${data.symptomFrequency === frequency.value
                      ? 'border-secondary bg-secondary' :'border-border'
                    }
                  `}>
                    {data.symptomFrequency === frequency.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-sm font-medium">{frequency.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Symptom Severity */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              How would you rate the severity of your symptoms?
            </label>
            <div className="space-y-2">
              {symptomSeverity.map((severity) => (
                <label
                  key={severity.value}
                  className={`
                    relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                    ${data.symptomSeverity === severity.value
                      ? 'border-accent bg-accent-50 text-accent-600' :'border-border bg-surface hover:border-border-focus'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="symptomSeverity"
                    value={severity.value}
                    checked={data.symptomSeverity === severity.value}
                    onChange={(e) => handleInputChange('symptomSeverity', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`
                    w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                    ${data.symptomSeverity === severity.value
                      ? 'border-accent bg-accent' :'border-border'
                    }
                  `}>
                    {data.symptomSeverity === severity.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-sm font-medium">{severity.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Symptom Triggers */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              What situations or factors tend to trigger or worsen your symptoms?
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {symptomTriggers.map((trigger) => (
                <label
                  key={trigger.value}
                  className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
                >
                  <input
                    type="checkbox"
                    checked={(data.symptomTriggers || []).includes(trigger.value)}
                    onChange={(e) => handleMultiSelectChange('symptomTriggers', trigger.value, e.target.checked)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <span className="ml-3 text-sm text-text-primary">{trigger.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Symptom Impact */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              How do these symptoms impact your daily life?
            </label>
            <textarea
              value={data.symptomImpact || ''}
              onChange={(e) => handleInputChange('symptomImpact', e.target.value)}
              placeholder="Describe how these symptoms affect your work, relationships, daily activities, etc..."
              rows={4}
              className="form-input resize-none"
            />
          </div>
        </>
      )}

      {/* Medical Attention */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Have you sought medical attention for any of these symptoms?
        </label>
        <div className="space-y-3">
          <label className="flex items-center p-4 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150">
            <input
              type="radio"
              name="medicalAttention"
              value="yes-diagnosed"
              checked={data.medicalAttention === 'yes-diagnosed'}
              onChange={(e) => handleInputChange('medicalAttention', e.target.value)}
              className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
            />
            <span className="ml-3 text-sm text-text-primary">Yes, and received a diagnosis</span>
          </label>
          <label className="flex items-center p-4 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150">
            <input
              type="radio"
              name="medicalAttention"
              value="yes-no-diagnosis"
              checked={data.medicalAttention === 'yes-no-diagnosis'}
              onChange={(e) => handleInputChange('medicalAttention', e.target.value)}
              className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
            />
            <span className="ml-3 text-sm text-text-primary">Yes, but no specific diagnosis</span>
          </label>
          <label className="flex items-center p-4 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150">
            <input
              type="radio"
              name="medicalAttention"
              value="planning-to"
              checked={data.medicalAttention === 'planning-to'}
              onChange={(e) => handleInputChange('medicalAttention', e.target.value)}
              className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
            />
            <span className="ml-3 text-sm text-text-primary">No, but planning to seek help</span>
          </label>
          <label className="flex items-center p-4 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150">
            <input
              type="radio"
              name="medicalAttention"
              value="no"
              checked={data.medicalAttention === 'no'}
              onChange={(e) => handleInputChange('medicalAttention', e.target.value)}
              className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
            />
            <span className="ml-3 text-sm text-text-primary">No, have not sought medical attention</span>
          </label>
        </div>
      </div>

      {/* Emergency Warning */}
      <div className="bg-error-50 border border-error-100 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={20} color="#EF4444" className="mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-error mb-2">
              When to Seek Immediate Medical Attention
            </h3>
            <p className="text-sm text-error mb-2">
              If you are experiencing any of the following, please seek immediate medical care:
            </p>
            <ul className="text-sm text-error space-y-1">
              <li>• Severe chest pain or pressure</li>
              <li>• Difficulty breathing or shortness of breath</li>
              <li>• Thoughts of self-harm or suicide</li>
              <li>• Severe panic attacks that don't subside</li>
              <li>• Any symptom that feels life-threatening</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SymptomsSection;